"use client";

import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { cfImageUrl } from "@/lib/cloudflare-image";
import type { ContestSponsor } from "../types";

/** A sponsor as any surface has it: the overlay's payload carries no link. */
export type SponsorLike = Pick<ContestSponsor, "name" | "logoImageId" | "tier"> & { url?: string | null };

/**
 * `EX-SPN01`, `docs/29` §6.23: a contest's sponsors as the contest page, the
 * standings head, the Big screen and the console's preview print them, title
 * sponsors under "Presented by" and the rest under "Partners", in the order
 * the server gives (title first, then the organizer's order).
 *
 * A logo is drawn whole on a white tile (never cropped to a circle), a sponsor
 * without one gets its initials. A link opens in a new tab with
 * `rel="noopener sponsored"`: the page is not vouching for the address, and
 * the sponsor's site never gets a handle on this one. The Big screen has no
 * links, nobody clicks a projector.
 */
export function SponsorStrip({
  sponsors,
  tone,
  size = "page",
  links = true,
  className,
}: {
  sponsors: readonly SponsorLike[];
  tone: "dark" | "light";
  /** `page` on the contest page and the preview, `compact` in the standings head, `screen` on the projector. */
  size?: "page" | "compact" | "screen";
  links?: boolean;
  className?: string;
}) {
  const t = useTranslations("contest.sponsors");
  if (sponsors.length === 0) return null;
  const title = sponsors.filter((sponsor) => sponsor.tier === "title");
  const partners = sponsors.filter((sponsor) => sponsor.tier !== "title");
  const label = cn(
    "shrink-0 font-semibold uppercase tracking-[0.12em]",
    size === "screen" ? "text-[15px]" : "text-[11px]",
    tone === "dark" ? "text-neutral-400" : "text-neutral-500",
  );
  return (
    <div
      data-testid="sponsor-strip"
      className={cn("flex flex-wrap items-center", size === "screen" ? "gap-x-9 gap-y-3" : "gap-x-5 gap-y-2.5", className)}
    >
      {title.length ? (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className={label}>{t("presentedBy")}</span>
          {title.map((sponsor) => (
            <Sponsor key={sponsor.name} sponsor={sponsor} tone={tone} size={size} lead links={links} />
          ))}
        </span>
      ) : null}
      {title.length && partners.length ? (
        <span aria-hidden className={cn("hidden w-px self-stretch sm:block", tone === "dark" ? "bg-white/15" : "bg-neutral-200")} />
      ) : null}
      {partners.length ? (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className={label}>{title.length ? t("partners") : t("supportedBy")}</span>
          {partners.map((sponsor) => (
            <Sponsor key={sponsor.name} sponsor={sponsor} tone={tone} size={size} links={links} />
          ))}
        </span>
      ) : null}
    </div>
  );
}

const TILE = {
  page: { lead: "h-9 w-9 text-[12px]", rest: "h-7 w-7 text-[10.5px]" },
  compact: { lead: "h-6 w-6 text-[9.5px]", rest: "h-6 w-6 text-[9.5px]" },
  screen: { lead: "h-[52px] w-[52px] text-[18px]", rest: "h-10 w-10 text-[14px]" },
} as const;

const NAME = {
  page: { lead: "text-[15px] font-bold", rest: "text-[13.5px] font-medium" },
  compact: { lead: "text-[12.5px] font-semibold", rest: "text-[12.5px] font-medium" },
  screen: { lead: "text-[26px] font-bold", rest: "text-[21px] font-medium" },
} as const;

/** "Nexa Labs" as "NL": the first letter of the first two words. */
export function sponsorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : (words[0] ?? "?").slice(0, 2)).toUpperCase();
}

function Sponsor({
  sponsor,
  tone,
  size,
  lead = false,
  links,
}: {
  sponsor: SponsorLike;
  tone: "dark" | "light";
  size: "page" | "compact" | "screen";
  lead?: boolean;
  links: boolean;
}) {
  const t = useTranslations("contest.sponsors");
  const logo = sponsor.logoImageId ? cfImageUrl(sponsor.logoImageId, "thumb") : "";
  const tile = cn(
    "flex shrink-0 items-center justify-center rounded-lg bg-white font-bold text-neutral-900",
    tone === "light" && "ring-1 ring-neutral-200",
    TILE[size][lead ? "lead" : "rest"],
  );
  const body = (
    <>
      {logo ? (
        // eslint-disable-next-line @next/next/no-img-element -- a Cloudflare Images delivery URL, already sized
        <img src={logo} alt="" className={cn(tile, "object-contain p-0.5")} />
      ) : (
        <span aria-hidden className={tile}>
          {sponsorInitials(sponsor.name)}
        </span>
      )}
      <span className={cn(NAME[size][lead ? "lead" : "rest"], tone === "dark" ? "text-white" : "text-neutral-900")}>{sponsor.name}</span>
    </>
  );
  const className = "inline-flex items-center gap-2";
  if (links && sponsor.url) {
    return (
      <a
        href={sponsor.url}
        target="_blank"
        rel="noopener sponsored"
        className={cn(
          className,
          "rounded-lg transition-opacity duration-200 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        )}
        aria-label={t("visit", { name: sponsor.name })}
        data-sponsor-link
      >
        {body}
      </a>
    );
  }
  return <span className={className}>{body}</span>;
}
